import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./icons";
import { useIsMobile } from "./hooks";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

// ───────────── Button ─────────────

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: "sm" | "md";
  loading?: boolean;
  icon?: IconName;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, block, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx("ui-btn", `ui-btn--${variant}`, `ui-btn--${size}`, block && "ui-btn--block", className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="ui-spinner" aria-hidden="true" /> : icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** 必填：无文字按钮的可访问名称 */
  label: string;
  badge?: number;
  variant?: "ghost" | "secondary" | "primary" | "danger";
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, badge, variant = "ghost", className, type = "button", ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cx("ui-iconbtn", `ui-btn--${variant}`, className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} />
      {badge ? (
        <span className="ui-iconbtn__badge" aria-hidden="true">
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </button>
  );
});

// ───────────── 表单 ─────────────

export function Field({ label, hint, error, children, id }: { label: string; hint?: string; error?: string; children: (id: string, describedBy?: string) => ReactNode; id?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  const descId = error || hint ? `${fid}-desc` : undefined;
  return (
    <div className={cx("ui-field", error && "ui-field--error")}>
      <label className="ui-field__label" htmlFor={fid}>
        {label}
      </label>
      {children(fid, descId)}
      {error ? (
        <div id={descId} className="ui-field__error" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div id={descId} className="ui-field__hint">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx("ui-input", className)} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx("ui-input", "ui-textarea", className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, ...rest }, ref) {
  return <select ref={ref} className={cx("ui-input", "ui-select", className)} {...rest} />;
});

/** 单选分段控件（role=radiogroup，方向键切换） */
export function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string; icon?: IconName }[]; onChange(v: T): void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const idx = options.findIndex((o) => o.value === value);
  const onKey = (e: KeyboardEvent) => {
    const d = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const n = (idx + d + options.length) % options.length;
    onChange(options[n]!.value);
    refs.current[n]?.focus();
  };
  return (
    <div className="ui-segmented" role="radiogroup" aria-label={label} onKeyDown={onKey}>
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          className={cx("ui-segmented__item", o.value === value && "is-active")}
          onClick={() => onChange(o.value)}
        >
          {o.icon ? <Icon name={o.icon} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ───────────── 徽标 / 状态 ─────────────

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger";

export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx("ui-badge", `ui-badge--${tone}`, className)}>{children}</span>;
}

export function StatusDot({ tone, label, pulse }: { tone: Tone; label: string; pulse?: boolean }) {
  return <span className={cx("ui-dot", `ui-dot--${tone}`, pulse && "ui-dot--pulse")} role="img" aria-label={label} title={label} />;
}

export function Spinner({ label }: { label: string }) {
  return (
    <span className="ui-spinner-wrap" role="status">
      <span className="ui-spinner" aria-hidden="true" />
      <span className="ui-sr-only">{label}</span>
    </span>
  );
}

export function EmptyState({ icon, title, children }: { icon?: IconName; title: string; children?: ReactNode }) {
  return (
    <div className="ui-empty">
      {icon ? <Icon name={icon} size={32} /> : null}
      <div className="ui-empty__title">{title}</div>
      {children}
    </div>
  );
}

// ───────────── 浮层基础：Portal + 焦点圈定 + Esc ─────────────

function useFocusTrap(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      Array.from(node?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? []);
    const first = node?.querySelector<HTMLElement>("[data-autofocus]") ?? focusables()[0];
    (first ?? node)?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
      } else if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const a = f[0]!;
        const z = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    node?.addEventListener("keydown", onKey);
    // 焦点所在元素被移除（例如列表项处理完消失）后焦点落到 body，Esc 仍要能关闭
    const onDocKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && (document.activeElement === document.body || document.activeElement === null)) closeRef.current();
    };
    document.addEventListener("keydown", onDocKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      node?.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onDocKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open]);
  return ref;
}

interface OverlayProps {
  open: boolean;
  onClose(): void;
  title: string;
  /** 关闭按钮的可访问名称（ui 不依赖 i18n，由调用方传入） */
  closeLabel: string;
  children: ReactNode;
  footer?: ReactNode;
}

/** 对话框；手机上自动改用底部 sheet */
export function Dialog(props: OverlayProps & { size?: "sm" | "md" | "lg" }) {
  const mobile = useIsMobile();
  if (mobile) return <Sheet {...props} />;
  return <DialogImpl {...props} />;
}

function DialogImpl({ open, onClose, title, closeLabel, children, footer, size = "md" }: OverlayProps & { size?: "sm" | "md" | "lg" }) {
  const ref = useFocusTrap(open, onClose);
  const tid = useId();
  if (!open) return null;
  return createPortal(
    <div className="ui-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={cx("ui-dialog", `ui-dialog--${size}`)} role="dialog" aria-modal="true" aria-labelledby={tid} tabIndex={-1}>
        <header className="ui-dialog__head">
          <h2 id={tid} className="ui-dialog__title">
            {title}
          </h2>
          <IconButton icon="close" label={closeLabel} onClick={onClose} />
        </header>
        <div className="ui-dialog__body">{children}</div>
        {footer ? <footer className="ui-dialog__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

/** 抽屉（平板上的侧栏等） */
export function Drawer({ open, onClose, title, closeLabel, children, side = "left" }: OverlayProps & { side?: "left" | "right" }) {
  const ref = useFocusTrap(open, onClose);
  const tid = useId();
  if (!open) return null;
  return createPortal(
    <div className="ui-overlay ui-overlay--drawer" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={cx("ui-drawer", `ui-drawer--${side}`)} role="dialog" aria-modal="true" aria-labelledby={tid} tabIndex={-1}>
        <header className="ui-dialog__head">
          <h2 id={tid} className="ui-dialog__title">
            {title}
          </h2>
          <IconButton icon="close" label={closeLabel} onClick={onClose} />
        </header>
        <div className="ui-drawer__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/** 底部 sheet：可下拉关闭 */
export function Sheet({ open, onClose, title, closeLabel, children, footer }: OverlayProps) {
  const ref = useFocusTrap(open, onClose);
  const tid = useId();
  const [dragY, setDragY] = useState(0);
  const start = useRef<number | null>(null);
  if (!open) return null;
  return createPortal(
    <div className="ui-overlay ui-overlay--sheet" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={ref}
        className="ui-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={tid}
        tabIndex={-1}
        style={dragY ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        <div
          className="ui-sheet__grip"
          aria-hidden="true"
          onTouchStart={(e) => (start.current = e.touches[0]?.clientY ?? null)}
          onTouchMove={(e) => start.current !== null && setDragY(Math.max(0, (e.touches[0]?.clientY ?? 0) - start.current))}
          onTouchEnd={() => {
            if (dragY > 80) onClose();
            setDragY(0);
            start.current = null;
          }}
        />
        <header className="ui-dialog__head">
          <h2 id={tid} className="ui-dialog__title">
            {title}
          </h2>
          <IconButton icon="close" label={closeLabel} onClick={onClose} />
        </header>
        <div className="ui-sheet__body">{children}</div>
        {footer ? <footer className="ui-dialog__foot ui-sheet__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

// ───────────── Tabs ─────────────

export interface TabItem {
  id: string;
  label: string;
  icon?: IconName;
  badge?: ReactNode;
  /** 标签可关闭时的关闭按钮名称 */
  closeLabel?: string;
  dirty?: boolean;
}

/** 标签栏（role=tablist；方向键 / Home / End 切换，Delete 关闭） */
export function Tabs({ items, value, onChange, onClose, label, className }: { items: TabItem[]; value: string | null; onChange(id: string): void; onClose?(id: string): void; label: string; className?: string }) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const onKey = (e: KeyboardEvent, i: number) => {
    let n = -1;
    if (e.key === "ArrowRight") n = (i + 1) % items.length;
    else if (e.key === "ArrowLeft") n = (i - 1 + items.length) % items.length;
    else if (e.key === "Home") n = 0;
    else if (e.key === "End") n = items.length - 1;
    else if ((e.key === "Delete" || (e.key === "w" && e.altKey)) && onClose && items[i]?.closeLabel) {
      e.preventDefault();
      onClose(items[i]!.id);
      return;
    }
    if (n < 0) return;
    e.preventDefault();
    const id = items[n]!.id;
    onChange(id);
    refs.current.get(id)?.focus();
  };
  return (
    <div className={cx("ui-tabs", className)} role="tablist" aria-label={label}>
      {items.map((it, i) => (
        <div key={it.id} className={cx("ui-tab", it.id === value && "is-active")}>
          <button
            ref={(el) => {
              if (el) refs.current.set(it.id, el);
              else refs.current.delete(it.id);
            }}
            type="button"
            role="tab"
            id={`tab-${it.id}`}
            aria-selected={it.id === value}
            tabIndex={it.id === value || (value === null && i === 0) ? 0 : -1}
            className="ui-tab__btn"
            onClick={() => onChange(it.id)}
            onKeyDown={(e) => onKey(e, i)}
            title={it.label}
          >
            {it.icon ? <Icon name={it.icon} size={16} /> : null}
            <span className="ui-tab__label">{it.label}</span>
            {it.dirty ? <span className="ui-tab__dirty" aria-hidden="true" /> : null}
            {it.badge}
          </button>
          {onClose && it.closeLabel ? (
            <button type="button" className="ui-tab__close" aria-label={it.closeLabel} tabIndex={-1} onClick={() => onClose(it.id)}>
              <Icon name="close" size={14} />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ───────────── Toast ─────────────

export interface ToastInput {
  message: string;
  tone?: Tone;
  /** 毫秒；0 = 不自动关闭 */
  duration?: number;
  action?: { label: string; onClick(): void };
}
interface ToastItem extends ToastInput {
  id: number;
}

const ToastCtx = createContext<{ show(t: ToastInput): number; dismiss(id: number): void } | null>(null);

export function ToastProvider({ children, dismissLabel }: { children: ReactNode; dismissLabel: string }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (t: ToastInput) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-3), { ...t, id }]);
      const d = t.duration ?? 4000;
      if (d > 0) setTimeout(() => dismiss(id), d);
      return id;
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="ui-toasts" role="region" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx("ui-toast", `ui-toast--${t.tone ?? "neutral"}`)} role={t.tone === "danger" ? "alert" : "status"}>
            <span className="ui-toast__msg">{t.message}</span>
            {t.action ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </Button>
            ) : null}
            <IconButton icon="close" label={dismissLabel} onClick={() => dismiss(t.id)} />
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const c = useContext(ToastCtx);
  if (!c) throw new Error("ToastProvider missing");
  return c;
}
